import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Person,
  ExpandMore,
  Fingerprint,
  Storage,
  CalendarToday,
  PersonOff,
} from "@mui/icons-material";
import {
  GetAllClientsOrderByEnum,
  UpdateClientStateRequestStateEnum,
} from "../../../../../api-client";
import { useGetAllClients } from "../../../../../stores/Taxation/taxation.queries";
import {
  useUpdateClient,
  useAuthenticateAdmin,
} from "../../../../../stores/Taxation/taxation.mutations";

const ClientSettings: React.FC = React.memo(() => {
  const [orderBy, setOrderBy] = useState<GetAllClientsOrderByEnum | undefined>(
    undefined,
  );
  const [deregisterConfirmOpen, setDeregisterConfirmOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const [selectedTssId, setSelectedTssId] = useState<string>("");

  // Use Tanstack Query to fetch Client data
  const {
    data: clientsList = [],
    isLoading,
    error,
  } = useGetAllClients(orderBy);

  // Mutations
  const updateClientMutation = useUpdateClient();
  const authenticateAdminMutation = useAuthenticateAdmin();

  const handleDeregisterClient = (clientId: string, tssId: string) => {
    setSelectedClientId(clientId);
    setSelectedTssId(tssId);
    setDeregisterConfirmOpen(true);
  };

  const handleConfirmDeregister = () => {
    if (!selectedClientId || !selectedTssId) return;

    // First authenticate admin with the TSS
    authenticateAdminMutation.mutate(selectedTssId, {
      onSuccess: () => {
        // After successful authentication, deregister the client
        updateClientMutation.mutate(
          {
            clientId: selectedClientId,
            state: UpdateClientStateRequestStateEnum.Deregistered,
          },
          {
            onSuccess: () => {
              setDeregisterConfirmOpen(false);
              setSelectedClientId("");
              setSelectedTssId("");
            },
          },
        );
      },
    });
  };

  const handleCancelDeregister = () => {
    setDeregisterConfirmOpen(false);
    setSelectedClientId("");
    setSelectedTssId("");
  };

  const getStatusChip = (status: string) => {
    const statusConfig: Record<
      string,
      {
        label: string;
        bgcolor: string;
        color: string;
      }
    > = {
      REGISTERED: {
        label: "Registriert",
        bgcolor: "#4caf50", // Green
        color: "#fff",
      },
      DEREGISTERED: {
        label: "Abgemeldet",
        bgcolor: "#f44336", // Red
        color: "#fff",
      },
    };

    const config = statusConfig[status] || {
      label: "Unbekannt",
      bgcolor: "#9e9e9e",
      color: "#fff",
    };

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          bgcolor: config.bgcolor,
          color: config.color,
          fontWeight: 600,
        }}
      />
    );
  };

  return (
    <Box sx={{ height: "100%", overflow: "auto", pb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            mb: 1,
            fontWeight: 600,
            background: "#003059",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Client Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Verwalten Sie Ihre Client-Konfigurationen
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12}>
          {/* Action Bar */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Alle Clients
              </Typography>

              {/* Filters */}
              <Stack direction="row" spacing={2}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Sortieren nach</InputLabel>
                  <Select
                    value={orderBy || ""}
                    label="Sortieren nach"
                    onChange={(e) =>
                      setOrderBy(
                        e.target.value
                          ? (e.target.value as GetAllClientsOrderByEnum)
                          : undefined,
                      )
                    }
                  >
                    <MenuItem value="">
                      <em>Standard</em>
                    </MenuItem>
                    <MenuItem value={GetAllClientsOrderByEnum.SerialNumber}>
                      Seriennummer
                    </MenuItem>
                    <MenuItem value={GetAllClientsOrderByEnum.TimeCreation}>
                      Erstellungsdatum
                    </MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          </Paper>

          {/* Client List */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Fehler beim Laden der Client-Konfigurationen
            </Alert>
          )}

          {isLoading ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <CircularProgress size={48} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Lade Client-Konfigurationen...
              </Typography>
            </Box>
          ) : clientsList.length > 0 ? (
            <Box
              sx={{
                maxHeight: "calc(100vh - 350px)",
                overflowY: "auto",
                pr: 1,
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#bdbdbd",
                  borderRadius: "4px",
                  "&:hover": {
                    background: "#757575",
                  },
                },
              }}
            >
              {clientsList.map((client, index) => (
                <Accordion
                  key={client._id}
                  sx={{
                    mb: 1,
                    "&:before": { display: "none" },
                    boxShadow: 1,
                    borderRadius: 1,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "#003059",
                      boxShadow: 3,
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore fontSize="small" />}
                    sx={{
                      minHeight: 48,
                      py: 0.5,
                      bgcolor: index % 2 === 0 ? "#ffffff" : "#f5f5f5",
                      "&:hover": {
                        bgcolor: "#d6ebfa",
                        "& .MuiTypography-root": {
                          color: "#1565c0",
                        },
                        "& .MuiSvgIcon-root": {
                          color: "#1565c0",
                        },
                      },
                      "& .MuiAccordionSummary-content": {
                        my: 0.5,
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      sx={{ width: "100%" }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          bgcolor: "#003059",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{ flexGrow: 1, justifyContent: "space-evenly" }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Fingerprint fontSize="small" color="inherit" />
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.875rem" }}
                          >
                            <strong>Seriennummer:</strong>{" "}
                            {client.serial_number || "N/A"}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Storage fontSize="small" color="inherit" />
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.875rem" }}
                          >
                            <strong>TSS ID:</strong>{" "}
                            {client.tss_id?.substring(0, 8) || "N/A"}...
                          </Typography>
                        </Stack>
                      </Stack>
                      {getStatusChip(client.state)}
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      bgcolor: "grey.50",
                      p: 3,
                      borderTop: "2px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Stack spacing={2} sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle1"
                        color="text.primary"
                        sx={{ fontWeight: 600 }}
                      >
                        Client-ID:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          bgcolor: "background.paper",
                          p: 1.5,
                          borderRadius: 1,
                          wordBreak: "break-all",
                        }}
                      >
                        {client._id}
                      </Typography>

                      <>
                        <Typography
                          variant="subtitle1"
                          color="text.primary"
                          sx={{ fontWeight: 600 }}
                        >
                          Beschreibung:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            bgcolor: "background.paper",
                            p: 1.5,
                            borderRadius: 1,
                            wordBreak: "break-all",
                          }}
                        >
                          {client.metadata?.description || "N/A"}
                        </Typography>
                      </>
                    </Stack>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2}>
                      {/* Serial Number */}
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, height: "100%" }}>
                          <Stack spacing={1}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Fingerprint fontSize="small" color="inherit" />
                              <Typography
                                variant="subtitle2"
                                color="text.primary"
                              >
                                Seriennummer
                              </Typography>
                            </Stack>
                            <Typography variant="body1" fontWeight={500}>
                              {client.serial_number || "N/A"}
                            </Typography>
                          </Stack>
                        </Paper>
                      </Grid>

                      {/* TSS ID */}
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, height: "100%" }}>
                          <Stack spacing={1}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Storage fontSize="small" color="inherit" />
                              <Typography
                                variant="subtitle2"
                                color="text.primary"
                              >
                                TSS ID
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              sx={{
                                fontFamily: "monospace",
                                wordBreak: "break-all",
                              }}
                            >
                              {client.tss_id || "N/A"}
                            </Typography>
                          </Stack>
                        </Paper>
                      </Grid>

                      {/* Creation Time */}
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2, height: "100%" }}>
                          <Stack spacing={1}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <CalendarToday fontSize="small" color="inherit" />
                              <Typography
                                variant="subtitle2"
                                color="text.primary"
                              >
                                Erstellungszeit
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body1"
                              fontWeight={400}
                              color="inherit"
                            >
                              {client.time_creation_formatted || "N/A"}
                            </Typography>
                          </Stack>
                        </Paper>
                      </Grid>

                      {/* Environment */}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="caption" color="text.secondary">
                            Umgebung: <strong>{client._env}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Status: {client.state}
                          </Typography>
                        </Stack>
                      </Grid>

                      {/* Action Buttons */}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Stack
                          direction="row"
                          spacing={2}
                          justifyContent="flex-end"
                        >
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<PersonOff />}
                            onClick={() =>
                              handleDeregisterClient(client._id, client.tss_id)
                            }
                            disabled={
                              updateClientMutation.isPending ||
                              authenticateAdminMutation.isPending ||
                              client.state === "DEREGISTERED"
                            }
                            sx={{
                              bgcolor: "#f44336",
                              "&:hover": {
                                bgcolor: "#d32f2f",
                              },
                            }}
                          >
                            Client abmelden
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ) : (
            <Paper sx={{ p: 6, textAlign: "center" }}>
              <Person sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                Keine Client-Konfigurationen gefunden
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Es sind noch keine Clients registriert
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>

      {/* Deregister Client Confirmation Dialog */}
      <Dialog
        open={deregisterConfirmOpen}
        onClose={handleCancelDeregister}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "#f44336" }}>
          Client abmelden bestätigen
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Achtung: Diese Aktion kann nicht rückgängig gemacht werden!
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Sind Sie sicher, dass Sie diesen Client abmelden möchten? Nach der
            Abmeldung kann der Client nicht mehr für Transaktionen verwendet
            werden.
          </Typography>
          {authenticateAdminMutation.isPending && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Admin wird authentifiziert...
            </Alert>
          )}
          {updateClientMutation.isPending && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Client wird abgemeldet...
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelDeregister} color="inherit">
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmDeregister}
            variant="contained"
            disabled={
              authenticateAdminMutation.isPending ||
              updateClientMutation.isPending
            }
            sx={{
              bgcolor: "#f44336",
              "&:hover": {
                bgcolor: "#d32f2f",
              },
            }}
          >
            {authenticateAdminMutation.isPending
              ? "Authentifizieren..."
              : updateClientMutation.isPending
                ? "Abmelden..."
                : "Ja, abmelden"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

ClientSettings.displayName = "ClientSettings";

export default ClientSettings;
